// @flow

import * as React from 'react';
import FlipMove from 'react-flip-move';
import { TabActiveContext } from '@ta/social.tabs';
import { ScrollAffordance } from '@ta/social.scroll';
import classNames from '@ta/common.classnames';
import { POIObject, useViewModelForObjects } from '@ta/social.poi';
import { InteractionLogger } from '@ta/tracking.interactions';

import styles from './POICarousel.less';

/* A type alias until we get proper flow-type generation */
type POILocation = any;
/* A miniature carousel of POI objects. Uses scrolling on mobile */
type Props = {|
  /** A list of POI's to show. */
  pois: POILocation[],
  /** Maximum number of pois to be displayed */
  limit?: number,
  /** Additional styling */
  className: string,
|};

const POICarousel = ({ className, pois: sortedPois, limit = 20 }: Props) => {
  // Don't need to use state since UI doesn't depend on this value!
  const [hasTrackedScroll, setHasTrackedScroll] = React.useState(false);
  const viewModels = useViewModelForObjects(sortedPois);
  const distance = 0;
  const snapToEdge = { left: 0, top: 0, bottom: 0, right: 100 };
  const threshold = 0;
  return (
    <div className={`${styles.relative} ${className}`}>
      <InteractionLogger>
        {trackScroll => (
          <TabActiveContext.Consumer>
            {tabData => (
              <ScrollAffordance
                top={false}
                bottom={false}
                left={sortedPois.length > 0} // Only show arrows when there's content to handle ReactJS bug with IE 11
                right={sortedPois.length > 0}
                threshold={threshold}
                minDistance={distance}
                snapToEdge={snapToEdge}
                className={`${styles.container} ${styles.carousel}`}
                isActive={tabData ? tabData.isActive : true}
                onScroll={
                  hasTrackedScroll
                    ? undefined
                    : e => {
                        setHasTrackedScroll(true);
                        trackScroll(e, 'linkedPoiScroll');
                      }
                }
              >
                {setChild => (
                  <FlipMove
                    staggerDurationBy="30"
                    duration={400}
                    maintainContainerHeight
                    typeName={null}
                    enterAnimation="fade"
                    leaveAnimation="fade"
                  >
                    {viewModels.slice(0, limit).map((model, index) => (
                      <div key={model.objectId}>
                        <div
                          ref={setChild(model.objectId)}
                          className={classNames(styles.gutter, {
                            [styles.first]: index === 0,
                            [styles.last]: index === sortedPois.length - 1,
                          })}
                        >
                          <div>
                            <POIObject poi={model} size="large" borderRadius="0" showSavesIcon />
                          </div>
                        </div>
                      </div>
                    ))}
                  </FlipMove>
                )}
              </ScrollAffordance>
            )}
          </TabActiveContext.Consumer>
        )}
      </InteractionLogger>
    </div>
  );
};

export default POICarousel;
